import type { TrainingDefaultsDto, UpdateTrainingDefaultsDto } from "@/client";

/**
 * The pure half of the account-defaults logic, kept out of the hook and the wizard so it can be
 * tested without React, react-query or the generated client. Types are imported type-only, so
 * nothing here pulls a runtime dependency.
 */

export type TrainingDefaultsField =
  | "clientImageUrl"
  | "serverImageUrl"
  | "config"
  | "autoDeploy";

/** The four fields an account default is made of - and the only ones any write path may touch. */
export const TRAINING_DEFAULT_FIELDS: TrainingDefaultsField[] = [
  "clientImageUrl",
  "serverImageUrl",
  "config",
  "autoDeploy",
];

/** Narrows an arbitrary object down to a defaults payload, so nothing else can leak into a save. */
export function pickTrainingDefaults(
  source: Partial<Record<TrainingDefaultsField, unknown>>,
): UpdateTrainingDefaultsDto {
  return {
    clientImageUrl: (source.clientImageUrl as string) ?? null,
    serverImageUrl: (source.serverImageUrl as string) ?? null,
    config: (source.config as string) ?? null,
    autoDeploy: (source.autoDeploy as boolean) ?? null,
  };
}

/**
 * Only the values the account actually stores. `defaults` also carries what a job would *fall back*
 * to (the newest published image, say), and treating those as stored turns any save into "freeze
 * today's fallback as an explicit default".
 */
export function storedTrainingDefaults(
  defaults: TrainingDefaultsDto | undefined,
): Partial<Record<TrainingDefaultsField, unknown>> {
  const stored: Partial<Record<TrainingDefaultsField, unknown>> = {};
  for (const field of TRAINING_DEFAULT_FIELDS) {
    if (defaults?.sources?.[field] === "accountDefault") {
      stored[field] = defaults[field];
    }
  }
  return stored;
}

/**
 * Nulls out every defaultable field the user never edited, so a schedule stores "ask the account
 * default" instead of a snapshot. A "run now" job keeps the literal -- it happens once, so what ran
 * should be recorded exactly.
 */
export function untouchedDefaultsAsNull(
  data: Record<string, unknown>,
  dirtyFields: Record<string, unknown> | undefined,
): Record<string, null> {
  const overrides: Record<string, null> = {};
  for (const field of TRAINING_DEFAULT_FIELDS) {
    if (data[field] !== undefined && !dirtyFields?.[field]) {
      overrides[field] = null;
    }
  }
  return overrides;
}
